export default class FormValidation {
    constructor(form, options = {}) {
        this.initialized = false;
        this.form = form;
        this.options = Object.assign({ showErrorsOnSubmit: true, clearErrorsOnFocus: true, errorMessageSelector: '.message', generalErrorSelector: '#form-error-message' }, options);
        this.init();
    }
    init() {
        if (this.initialized || !this.form) {
            return;
        }
        if (this.options.clearErrorsOnFocus) {
            this.initClearErrorsOnFocus();
        }
        this.initialized = true;
    }
    initClearErrorsOnFocus() {
        const inputs = this.form.querySelectorAll('input, select, textarea');
        inputs.forEach(input => {
            input.addEventListener('focus', () => {
                const fieldName = input.getAttribute('name') || '';
                const formField = this.findFormField(fieldName);
                // dont clear errors when focusing on a field that already has an error
                // this prevents clearing when focus on error fields
                if (formField && !formField.classList.contains('error')) {
                    this.clearFieldError(fieldName);
                }
            });
            // clear error when user starts typing in an error field
            input.addEventListener('input', () => {
                const fieldName = input.getAttribute('name') || '';
                const formField = this.findFormField(fieldName);
                if (formField && formField.classList.contains('error')) {
                    this.clearFieldError(fieldName);
                }
            });
        });
    }
    /**
     * Display validation errors from server response
     * @param focusFirstError - Whether to focus on the first error field
     */
    showErrors(response, focusFirstError = true) {
        var _a, _b, _c;
        this.clearAllErrors();
        if ((_a = response.data) === null || _a === void 0 ? void 0 : _a.errors) {
            Object.entries(response.data.errors).forEach(([field, message]) => {
                if (field === '_general') {
                    this.showGeneralError(message);
                }
                else {
                    this.showFieldError(field, message);
                }
            });
            // focus on first error field after all errors are displayed
            if (focusFirstError && Object.keys(response.data.errors).length > 0) {
                setTimeout(() => {
                    this.focusFirstErrorField();
                }, 100);
            }
        }
        if ((_b = response.data) === null || _b === void 0 ? void 0 : _b.general_error) {
            this.showGeneralError(response.data.general_error);
        }
        if (((_c = response.data) === null || _c === void 0 ? void 0 : _c.message) && !response.success) {
            this.showGeneralError(response.data.message);
        }
    }
    /**
     * Show error for specific field
     */
    showFieldError(fieldName, message) {
        const formField = this.findFormField(fieldName);
        if (!formField) {
            return;
        }
        formField.classList.add('error');
        // get input element
        const input = formField.querySelector('input, select, textarea');
        if (input) {
            input.setAttribute('aria-invalid', 'true');
            // add error id to aria-describedby without removing existing ones
            const errorId = input.id + '-error';
            const currentDescribedBy = input.getAttribute('aria-describedby') || '';
            const describedIds = currentDescribedBy.split(' ').filter(id => id && id !== errorId);
            describedIds.push(errorId);
            input.setAttribute('aria-describedby', describedIds.join(' ').trim());
        }
        const messageElement = formField.querySelector(this.options.errorMessageSelector);
        if (messageElement) {
            messageElement.textContent = message;
            messageElement.setAttribute('role', 'alert');
            messageElement.setAttribute('aria-live', 'assertive');
        }
        this.scrollToFirstError();
    }
    /**
     * Clear error for specific field
     */
    clearFieldError(fieldName) {
        const formField = this.findFormField(fieldName);
        if (!formField) {
            return;
        }
        formField.classList.remove('error');
        // get input element
        const input = formField.querySelector('input, select, textarea');
        if (input) {
            input.removeAttribute('aria-invalid');
            // remove error id from aria-describedby while preserving others
            const errorId = input.id + '-error';
            const currentDescribedBy = input.getAttribute('aria-describedby');
            if (currentDescribedBy) {
                const describedIds = currentDescribedBy.split(' ').filter(id => id && id !== errorId);
                if (describedIds.length > 0) {
                    input.setAttribute('aria-describedby', describedIds.join(' ').trim());
                }
                else {
                    input.removeAttribute('aria-describedby');
                }
            }
        }
        const messageElement = formField.querySelector(this.options.errorMessageSelector);
        if (messageElement) {
            messageElement.textContent = '';
            messageElement.removeAttribute('role');
            messageElement.removeAttribute('aria-live');
        }
    }
    /**
     * Focus on first error field without clearing it
     */
    focusFirstErrorField() {
        const firstError = this.form.querySelector('.form-field.error');
        if (firstError) {
            const input = firstError.querySelector('input, select, textarea');
            if (input) {
                input.focus();
            }
        }
    }
    /**
     * Show general error message
     */
    showGeneralError(message) {
        const errorContainer = document.querySelector(this.options.generalErrorSelector);
        if (errorContainer) {
            errorContainer.textContent = message;
            errorContainer.classList.remove('hidden');
            errorContainer.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }
    /**
     * Clear general error message
     */
    clearGeneralError() {
        const errorContainer = document.querySelector(this.options.generalErrorSelector);
        if (errorContainer) {
            errorContainer.textContent = '';
            errorContainer.classList.add('hidden');
        }
    }
    /**
     * Clear all validation errors
     */
    clearAllErrors() {
        this.clearGeneralError();
        const errorFields = this.form.querySelectorAll('.form-field.error');
        errorFields.forEach(field => {
            field.classList.remove('error');
            const input = field.querySelector('input, select, textarea');
            if (input) {
                input.removeAttribute('aria-invalid');
                const currentDescribedBy = input.getAttribute('aria-describedby');
                if (currentDescribedBy) {
                    const errorId = input.id + '-error';
                    const describedIds = currentDescribedBy.split(' ').filter(id => id && id !== errorId);
                    if (describedIds.length > 0) {
                        input.setAttribute('aria-describedby', describedIds.join(' ').trim());
                    }
                    else {
                        input.removeAttribute('aria-describedby');
                    }
                }
            }
            const messageElement = field.querySelector(this.options.errorMessageSelector);
            if (messageElement) {
                messageElement.textContent = '';
                messageElement.removeAttribute('role');
                messageElement.removeAttribute('aria-live');
            }
        });
    }
    /**
     * Find form field container for given field name
     */
    findFormField(fieldName) {
        const input = this.form.querySelector(`[name="${fieldName}"]`);
        if (!input) {
            return null;
        }
        return input.closest('.form-field');
    }
    /**
     * Scroll to first error field
     */
    scrollToFirstError() {
        const firstError = this.form.querySelector('.form-field.error');
        if (firstError) {
            firstError.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }
    /**
     * Check if form has any errors
     */
    hasErrors() {
        return this.form.querySelectorAll('.form-field.error').length > 0;
    }
    /**
     * Get all field names with errors
     */
    getErrorFields() {
        const errorFields = [];
        const errorElements = this.form.querySelectorAll('.form-field.error [name]');
        errorElements.forEach(element => {
            const name = element.getAttribute('name');
            if (name) {
                errorFields.push(name);
            }
        });
        return errorFields;
    }
}
